import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Menu } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { themeStore } from "@/stores";
import { ThemeSwitcher } from "@/components/layout";
import { LanguageSwitcher } from "@/components/common";
import { NotificationsBell } from "@/features/notifications";
import UserMenu from "./UserMenu";

const Navbar = observer(
  ({
    sidebarCollapsed,
    onMobileMenuToggle,
    showBrand = false,
    showMobileMenuButton = true,
  }) => {
    const { t } = useTran();
    const [elevated, setElevated] = useState(false);
    const currentTheme = themeStore.theme;

    // Add shadow on scroll
    useEffect(() => {
      const onScroll = () => setElevated(window.scrollY > 2);
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Theme-based classes
    const baseBg = "bg-card";
    const backdrop =
      "backdrop-blur supports-[backdrop-filter]:backdrop-blur-md";
    const borderClr = "border-border";
    const shadow = elevated ? "shadow-sm" : "";
    const shouldShowBrand = showBrand || sidebarCollapsed;

    return (
      <header
        className={cn(
          "sticky top-0 z-40 border-b transition-shadow",
          baseBg,
          backdrop,
          borderClr,
          shadow,
        )}
        data-sidebar-state={sidebarCollapsed ? "collapsed" : "expanded"}
        data-theme={currentTheme}
      >
        <div className="relative mx-auto flex h-14 w-full items-center justify-between gap-3 px-3 sm:px-4 lg:px-6 xl:px-8">
          {/* Left: Hamburger for mobile */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {showMobileMenuButton ? (
              <button
                type="button"
                className="rounded-radius p-2 focus:outline-none lg:hidden"
                onClick={onMobileMenuToggle}
                aria-label={t("Toggle mobile menu")}
              >
                <Menu className="h-5 w-5" />
              </button>
            ) : null}

            {/* Show brand only when sidebar is collapsed on desktop */}
            {shouldShowBrand && (
              <div className="flex min-w-0 items-center">
                <img
                  src="/logos/logo.svg"
                  alt={t("NxStart Logo")}
                  className="h-10 w-auto sm:h-11"
                />
                {/* <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[8px] font-semibold tracking-wide text-primary"> */}
                {/*   Sustainability */}
                {/* </span> */}
              </div>
            )}
          </div>

          {/* Right: Menus */}
          <div className="flex flex-none items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <NotificationsBell />
            <UserMenu />
          </div>
        </div>
      </header>
    );
  },
);

export default Navbar;
